import React, { useMemo, useState, ReactElement } from 'react';
import { LocationBreakdownRow } from '@Apis/companyDashboard';
import { scoreBand } from './Tiles/ScoreBlock';
import './LocationBreakdownTable.scss';

// The sortable columns, in render order. Column order and visibility are fixed — there is no
// user customization in this story.
export type BreakdownSortKey =
    | 'groupName'
    | 'healthScore'
    | 'riskScore'
    | 'pastDueMilestoneCount'
    | 'upcomingMilestoneCount'
    | 'totalUnassignedCount';

export type SortDirection = 'asc' | 'desc';

interface ColumnDef {
    key: BreakdownSortKey;
    label: string;
    isNumeric: boolean;
}

const COLUMNS: ColumnDef[] = [
    { key: 'groupName', label: 'Name', isNumeric: false },
    { key: 'healthScore', label: 'Health', isNumeric: true },
    { key: 'riskScore', label: 'Risk', isNumeric: true },
    { key: 'pastDueMilestoneCount', label: 'Past-Due Milestones', isNumeric: true },
    { key: 'upcomingMilestoneCount', label: 'Upcoming Deadlines', isNumeric: true },
    { key: 'totalUnassignedCount', label: 'Unassigned Work', isNumeric: true },
];

// Shown in place of the empty name the endpoint sends for the "no value for this grouping"
// bucket. Exported so the sidebar and its tests can name the same thing the table does.
export const UNASSIGNED_GROUP_NAME = 'Unassigned';

export const DEFAULT_SORT_KEY: BreakdownSortKey = 'groupName';
export const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

// Nothing to report reads as an em dash, not as a zero: a column of 0s draws the eye to the
// groups with no work in it. Sorting still uses the real number.
const formatCount = (count: number): string | number => (count ? count : '—');

const numericValue = (row: LocationBreakdownRow, key: BreakdownSortKey): number | null => {
    const value = row[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

/**
 * Orders the rows by one column. A missing score is not a low score, so unscored groups sink
 * to the bottom in both directions rather than pretending to be a 0 or a 10; ties fall back to
 * the name so the order is stable no matter which column is sorted.
 */
const sortRows = (
    rows: LocationBreakdownRow[],
    key: BreakdownSortKey,
    direction: SortDirection,
    isNumeric: boolean
): LocationBreakdownRow[] => {
    const sign = direction === 'asc' ? 1 : -1;
    const byName = (a: LocationBreakdownRow, b: LocationBreakdownRow) =>
        (a.groupName ?? '').localeCompare(b.groupName ?? '', undefined, { sensitivity: 'base' });

    return [...rows].sort((a, b) => {
        if (!isNumeric) return sign * byName(a, b);

        const left = numericValue(a, key);
        const right = numericValue(b, key);
        if (left === null && right === null) return byName(a, b);
        if (left === null) return 1;
        if (right === null) return -1;
        if (left === right) return byName(a, b);
        return sign * (left - right);
    });
};

interface ScoreBadgeProps {
    score?: number | null;
    unavailableReason?: string | null;
    // Health is higher-is-better; Risk is a magnitude (1 low, 10 high) and inverts the bands.
    higherIsBetter: boolean;
}

// The compact form of the location row's ScoreBlock: same bands, same colours, no label —
// the column header already names the metric.
const ScoreBadge = ({ score, unavailableReason, higherIsBetter }: ScoreBadgeProps): ReactElement => {
    const numericScore = typeof score === 'number' && Number.isFinite(score) ? score : null;

    if (numericScore === null) {
        return (
            <span className='lbt-score lbt-score--placeholder' title={unavailableReason ?? undefined}>
                —
            </span>
        );
    }

    return (
        <span className={`lbt-score lbt-score--${scoreBand(numericScore, higherIsBetter)}`}>
            {numericScore.toFixed(1)}
        </span>
    );
};

interface LocationBreakdownTableProps {
    rows: LocationBreakdownRow[];
    /** Names the grouping in the caption, e.g. "Discipline". */
    groupLabel: string;
    /** Drills into one group. Omitted, the names render as plain text. */
    onGroupSelect?: (row: LocationBreakdownRow) => void;
}

const LocationBreakdownTable = ({ rows, groupLabel, onGroupSelect }: LocationBreakdownTableProps): ReactElement => {

    const [sortKey, setSortKey] = useState<BreakdownSortKey>(DEFAULT_SORT_KEY);
    const [sortDirection, setSortDirection] = useState<SortDirection>(DEFAULT_SORT_DIRECTION);

    // The endpoint sends `groupName: null` for the bucket of work with no value for the
    // current grouping, which the schema turns into '' (see nullableText in
    // companyDashboard.types.ts). An empty name used to be a blank cell; now that the name is
    // the drill-in control it would be a zero-width button that a mouse cannot hit and a
    // screen reader announces as a bare "button". Substituted here rather than at render so
    // the row sorts under the name it displays, and so the selection handed to the sidebar
    // carries it too.
    const namedRows = useMemo(
        () => rows.map((row) => (row.groupName ? row : { ...row, groupName: UNASSIGNED_GROUP_NAME })),
        [rows]
    );

    const sortedRows = useMemo(() => {
        const column = COLUMNS.find((candidate) => candidate.key === sortKey) ?? COLUMNS[0];
        return sortRows(namedRows, column.key, sortDirection, column.isNumeric);
    }, [namedRows, sortKey, sortDirection]);

    const onHeaderClick = (key: BreakdownSortKey) => {
        if (key === sortKey) {
            setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
            return;
        }
        // A new column starts ascending: A-Z for the name, smallest-first for the counts.
        setSortKey(key);
        setSortDirection('asc');
    };

    return (
        // The scroll box is what makes the header sticky: `position: sticky` resolves against
        // the nearest scrolling ancestor, and the panel itself does not scroll.
        <div className='lbt-scroll'>
            <table className='lbt-table'>
                <caption>Breakdown by {groupLabel}</caption>
                <thead>
                    <tr>
                        {COLUMNS.map((column) => {
                            const isSorted = column.key === sortKey;
                            return (
                                <th
                                    key={column.key}
                                    scope='col'
                                    className={column.isNumeric ? 'lbt-col--numeric' : undefined}
                                    aria-sort={isSorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                    <button
                                        type='button'
                                        className={`lbt-sort${isSorted ? ' lbt-sort--active' : ''}`}
                                        onClick={() => onHeaderClick(column.key)}>
                                        <span className='lbt-sort__label'>{column.label}</span>
                                        {/* Carets, not arrows: fa-arrow-up/down are not in the
                                            bundled FA6 subset and render as tofu. */}
                                        <i
                                            className={`fa-solid fa-caret-${isSorted && sortDirection === 'desc' ? 'down' : 'up'} lbt-sort__icon`}
                                            aria-hidden='true'
                                        />
                                    </button>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {sortedRows.map((row) => (
                        <tr key={row.groupId}>
                            <td className='lbt-name' title={row.groupName}>
                                {onGroupSelect ? (
                                    <button
                                        type='button'
                                        className='lbt-name__link'
                                        onClick={() => onGroupSelect(row)}>
                                        {row.groupName}
                                    </button>
                                ) : (
                                    row.groupName
                                )}
                            </td>
                            <td className='lbt-col--numeric'>
                                <ScoreBadge
                                    score={row.healthScore}
                                    unavailableReason={row.scoresUnavailableReason}
                                    higherIsBetter
                                />
                            </td>
                            <td className='lbt-col--numeric'>
                                <ScoreBadge
                                    score={row.riskScore}
                                    unavailableReason={row.scoresUnavailableReason}
                                    higherIsBetter={false}
                                />
                            </td>
                            {/* Plain numbers: drilling in from a count, rather than from the
                                group name, is a later story. */}
                            <td className={`lbt-col--numeric${row.pastDueMilestoneCount ? ' lbt-count--danger' : ''}`}>
                                {formatCount(row.pastDueMilestoneCount)}
                            </td>
                            <td className='lbt-col--numeric'>{formatCount(row.upcomingMilestoneCount)}</td>
                            <td className='lbt-col--numeric'>{formatCount(row.totalUnassignedCount)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default LocationBreakdownTable;
